// src/portal-app/more/HelpScreen.js
//
// W5 Task 6 — Help screen (AI chat).
//
// A full-screen chat interface for the Aqana AI help centre.
//
// Zones:
//   Chat area    — Scrollable list of message bubbles. User messages aligned
//                  to inline-end (brand bg, white text). Assistant messages
//                  aligned to inline-start (glass bg, ink text). Initial
//                  state shows a welcome assistant bubble.
//   Typing ind.  — Three animated dots bubble (assistant position) while the
//                  AI processes.
//   Error bubble — Error assistant bubble with Retry button on chat() failure.
//   Input area   — Fixed at bottom (above safe area + tab bar). Glass-styled
//                  text input + send button. Disabled while loading.
//   Create ticket— Trailing action via useNavChrome. Opens ConfirmSheet with
//                  last assistant content as the summary. On confirm →
//                  createTicket(summary, history) → success toast element.
//                  On failure → inline role="alert" error INSIDE the sheet.
//
// ConfirmSheet fix — REAL BUG (same defect TransactionsScreen, EodScreen,
// ProductsScreen, InventoryScreen and DevicesScreen each fixed for their own
// flows). The ticket sheet passed `error={ticketError}`. ConfirmSheet has
// never accepted an `error` prop — React dropped it silently, so
// handleTicketConfirm()'s setTicketError() wrote state that nothing rendered
// and a failed support-ticket creation showed the user absolutely nothing.
// ConfirmSheet's real error slot is `children`, rendered inside the sheet
// above the action row (see its header: "anything rendered as a sibling of
// ConfirmSheet instead sits behind its fixed, z-indexed backdrop and is
// invisible in the live app"). Moved there, with role="alert" so a screen
// reader announces it, and an inline SVG so the error does not read by
// colour alone.
//
// Fixed in passing: that error used the 'chat_error' key with a ticket-
// specific English fallback — the same shared-key defect ProductsScreen's
// empty state had. Once 'chat_error' resolves from the DB it renders the
// chat-failure sentence, not the ticket one. It now has its own key,
// 'ticket_error'.
//
// Bold parsing: **bold** → <strong> elements (same renderBold() as NarrativeCard
// in insights/kit.js). Never uses dangerouslySetInnerHTML.
//
// RTL-safe: user bubbles use margin-inline-start: auto (align to inline-end),
// assistant bubbles use margin-inline-end: auto (align to inline-start).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { helpCenterService } from '../../services/api';
import { ConfirmSheet } from '../operations/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.hlp-wrap{
  width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  display:flex;flex-direction:column;
  padding-block-end:calc(72px + var(--safe-bottom,0px));
}

/* ---- chat scroll area ---- */
.hlp-messages{
  flex:1;
  display:flex;flex-direction:column;
  gap:10px;
  padding:12px 16px 4px;
}

/* ---- bubble shared ---- */
.hlp-bubble-row{display:flex;width:100%;}
.hlp-bubble-row--user{justify-content:flex-end;}
.hlp-bubble-row--assistant{justify-content:flex-start;}
.hlp-bubble-row--error{justify-content:flex-start;}

.hlp-bubble{
  max-width:82%;
  padding:12px 14px;
  border-radius:18px;
  font-size:14px;
  line-height:1.5;
  font-family:inherit;
  word-break:break-word;
}

/* user bubble */
.hlp-bubble--user{
  border-radius:18px 18px 4px 18px;
  background:var(--c-green);
  color:var(--c-white);
}

/* assistant bubble */
.hlp-bubble--assistant{
  border-radius:18px 18px 18px 4px;
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  backdrop-filter:blur(12px) saturate(1.6);
  -webkit-backdrop-filter:blur(12px) saturate(1.6);
  border:1px solid color-mix(in srgb,var(--c-surface) 50%,transparent);
  color:var(--c-ink);
}

/* error bubble */
.hlp-bubble--error{
  border-radius:18px 18px 18px 4px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  color:var(--c-red);
  display:flex;flex-direction:column;gap:8px;
}

/* ---- typing indicator ---- */
.hlp-typing{display:flex;align-items:center;gap:5px;padding:14px 16px;}
.hlp-dot{
  width:7px;height:7px;border-radius:50%;
  background:var(--c-textLight);
  animation:hlpDot 1.2s infinite ease-in-out;
  flex-shrink:0;
}
.hlp-dot:nth-child(2){animation-delay:.2s;}
.hlp-dot:nth-child(3){animation-delay:.4s;}
@keyframes hlpDot{
  0%,80%,100%{transform:scale(.8);opacity:.4;}
  40%{transform:scale(1.1);opacity:1;}
}
@media(prefers-reduced-motion:reduce){
  .hlp-dot{animation:none;opacity:.6;}
}

/* ---- retry button (inside error bubble) ---- */
.hlp-retry-btn{
  display:inline-flex;align-items:center;gap:5px;
  min-height:32px;padding:0 12px;border-radius:8px;
  border:1.5px solid color-mix(in srgb,var(--c-red) 50%,transparent);
  background:color-mix(in srgb,var(--c-red) 10%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-red);cursor:pointer;
  transition:background .12s ease;align-self:flex-start;
}
.hlp-retry-btn:hover{background:color-mix(in srgb,var(--c-red) 16%,transparent);}
.hlp-retry-btn:active{transform:scale(.96);}

/* ---- ticket success notification ---- */
.hlp-success{
  margin:0 16px 8px;padding:12px 14px;border-radius:14px;
  background:color-mix(in srgb,var(--c-green) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--c-green) 25%,transparent);
  display:flex;align-items:center;gap:8px;
  font-size:13px;font-weight:600;color:var(--c-green);
}

/* ---- ticket error — rendered INSIDE ConfirmSheet as children (its real
   error slot; a sibling would sit behind the sheet's fixed, z-indexed
   backdrop and never be seen). The inline SVG alongside the text means the
   error does not rely on red alone to read as an error. ---- */
.hlp-ticket-error{
  margin-block-start:10px;padding:10px 12px;border-radius:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--c-red) 28%,transparent);
  display:flex;align-items:flex-start;gap:8px;
  font-size:12px;font-weight:600;color:var(--c-red);text-align:start;
}
.hlp-ticket-error svg{flex:none;margin-block-start:1px;}

/* ---- input bar (sticky above tab bar) ---- */
.hlp-input-bar{
  position:sticky;bottom:0;
  padding:10px 12px;
  padding-block-end:calc(10px + var(--safe-bottom,0px));
  background:color-mix(in srgb,var(--c-bg) 85%,transparent);
  backdrop-filter:blur(16px) saturate(1.6);
  -webkit-backdrop-filter:blur(16px) saturate(1.6);
  border-block-start:1px solid color-mix(in srgb,var(--c-border) 60%,transparent);
  display:flex;align-items:center;gap:8px;
}

.hlp-input{
  flex:1;
  min-height:44px;
  padding:10px 14px;
  border-radius:22px;
  border:1.5px solid color-mix(in srgb,var(--c-border) 70%,transparent);
  background:color-mix(in srgb,var(--c-surface) 35%,transparent);
  font-family:inherit;font-size:14px;color:var(--c-ink);
  outline:none;resize:none;
  transition:border-color .15s ease;
  box-sizing:border-box;
}
.hlp-input:focus{
  border-color:color-mix(in srgb,var(--c-green) 60%,transparent);
}
.hlp-input::placeholder{color:var(--c-textLight);}
.hlp-input:disabled{opacity:.5;cursor:not-allowed;}

.hlp-send-btn{
  width:44px;height:44px;flex-shrink:0;
  border-radius:50%;
  border:none;
  background:var(--c-green);
  color:var(--c-white);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;
  transition:background .12s ease,transform .12s ease;
}
.hlp-send-btn:hover:not(:disabled){background:color-mix(in srgb,var(--c-green) 85%,var(--c-ink));}
.hlp-send-btn:active:not(:disabled){transform:scale(.92);}
.hlp-send-btn:disabled{opacity:.4;cursor:not-allowed;}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'hlp-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Bold parser — safe alternative to dangerouslySetInnerHTML.          */
/* Splits on **...** markers; odd indices are bold segments.           */
/* ------------------------------------------------------------------ */
function renderBold(text) {
    const parts = String(text).split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
    );
}

/* ------------------------------------------------------------------ */
/* Send icon SVG                                                       */
/* ------------------------------------------------------------------ */
const SendIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Ticket icon SVG                                                     */
/* ------------------------------------------------------------------ */
const TicketIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M2 9a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a1 1 0 0 0 0 2v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1a1 1 0 0 0 0-2V9z" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Message bubble                                                       */
/* ------------------------------------------------------------------ */
function MessageBubble({ message }) {
    const { role, content, isError, onRetry } = message;

    if (isError) {
        return (
            <div className="hlp-bubble-row hlp-bubble-row--error">
                <div
                    className="hlp-bubble hlp-bubble--error"
                    data-testid="bubble-error"
                >
                    <span>{content}</span>
                    {onRetry && (
                        <button
                            type="button"
                            className="hlp-retry-btn"
                            onClick={onRetry}
                            data-testid="retry-btn"
                        >
                            {/* Retry label injected at use-site via content */}
                            {message.retryLabel || 'Retry'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (role === 'user') {
        return (
            <div className="hlp-bubble-row hlp-bubble-row--user">
                <div
                    className="hlp-bubble hlp-bubble--user"
                    data-testid="bubble-user"
                >
                    {content}
                </div>
            </div>
        );
    }

    // assistant
    return (
        <div className="hlp-bubble-row hlp-bubble-row--assistant">
            <div
                className="hlp-bubble hlp-bubble--assistant"
                data-testid="bubble-assistant"
            >
                {renderBold(content)}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Typing indicator                                                    */
/* ------------------------------------------------------------------ */
function TypingIndicator() {
    return (
        <div className="hlp-bubble-row hlp-bubble-row--assistant">
            <div
                className="hlp-bubble hlp-bubble--assistant hlp-typing"
                data-testid="typing-indicator"
                aria-label="AI is typing"
            >
                <span className="hlp-dot" aria-hidden="true" />
                <span className="hlp-dot" aria-hidden="true" />
                <span className="hlp-dot" aria-hidden="true" />
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* HelpScreen                                                          */
/* ------------------------------------------------------------------ */
export default function HelpScreen() {
    ensureCss();
    const { t } = useLanguage();

    // messages: [{ id, role: 'user'|'assistant', content, isError?, onRetry?, retryLabel? }]
    const [messages, setMessages] = useState(() => [{
        id: 'welcome',
        role: 'assistant',
        content: t('help_welcome', {}, 'Ask me anything about managing your business with Aqana.'),
    }]);

    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Create ticket state
    const [ticketSheetOpen, setTicketSheetOpen] = useState(false);
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketError, setTicketError] = useState(null);
    const [ticketSuccess, setTicketSuccess] = useState(false);

    // Pending retry message (last user message text + its index in messages)
    const lastUserMsgRef = useRef(null);

    // Auto-scroll ref
    const bottomRef = useRef(null);

    // Build conversation history (role + content pairs, excluding error entries)
    const buildHistory = useCallback((msgs) =>
        msgs
            .filter((m) => !m.isError && (m.role === 'user' || m.role === 'assistant'))
            .map(({ role, content }) => ({ role, content })),
    []);

    // Last assistant content (for ticket summary)
    const lastAssistantContent = messages
        .slice()
        .reverse()
        .find((m) => m.role === 'assistant' && !m.isError)
        ?.content || '';

    /* Auto-scroll to bottom on new messages */
    useEffect(() => {
        if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    /* ---- send message ---- */
    const sendMessage = useCallback(async (text, historyOverride) => {
        const msgText = (text || inputValue).trim();
        if (!msgText || isLoading) return;

        // Build current history BEFORE adding the new user message
        const currentMsgs = historyOverride || messages;
        const history = buildHistory(currentMsgs);

        // Add user message
        const userMsg = { id: `u-${Date.now()}`, role: 'user', content: msgText };
        const nextMsgs = [...currentMsgs.filter((m) => !m.isError), userMsg];
        setMessages(nextMsgs);
        setInputValue('');
        setIsLoading(true);
        lastUserMsgRef.current = { text: msgText, history };

        try {
            const result = await helpCenterService.chat(msgText, history);
            const assistantMsg = {
                id: `a-${Date.now()}`,
                role: 'assistant',
                content: result.response || t('chat_error', {}, 'Something went wrong. Please try again.'),
            };
            setMessages((prev) => [...prev, assistantMsg]);
        } catch {
            const retryData = lastUserMsgRef.current;
            setMessages((prev) => [
                ...prev,
                {
                    id: `e-${Date.now()}`,
                    role: 'assistant',
                    isError: true,
                    content: t('chat_error', {}, 'Something went wrong. Please try again.'),
                    retryLabel: t('retry_message', {}, 'Retry'),
                    onRetry: () => {
                        // Remove the error entry and re-send
                        setMessages((curr) => curr.filter((m) => !m.isError));
                        sendMessage(retryData.text, curr => curr.filter(m => !m.isError));
                    },
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, isLoading, messages, buildHistory, t]);

    /* ---- retry handler ---- */
    const handleRetry = useCallback(() => {
        if (!lastUserMsgRef.current) return;
        const { text } = lastUserMsgRef.current;
        // Remove error messages, keep rest
        const cleanMsgs = messages.filter((m) => !m.isError);
        setMessages(cleanMsgs);
        // Re-invoke send with the stored text against clean history
        const history = buildHistory(cleanMsgs);

        const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text };
        const nextMsgs = [...cleanMsgs, userMsg];
        setMessages(nextMsgs);
        setIsLoading(true);

        helpCenterService.chat(text, history)
            .then((result) => {
                const assistantMsg = {
                    id: `a-${Date.now()}`,
                    role: 'assistant',
                    content: result.response || t('chat_error', {}, 'Something went wrong. Please try again.'),
                };
                setMessages((prev) => [...prev, assistantMsg]);
            })
            .catch(() => {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `e-${Date.now()}`,
                        role: 'assistant',
                        isError: true,
                        content: t('chat_error', {}, 'Something went wrong. Please try again.'),
                        retryLabel: t('retry_message', {}, 'Retry'),
                        onRetry: handleRetry,
                    },
                ]);
            })
            .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, buildHistory, t]);

    /* ---- attach retry handler to last error bubble ---- */
    // We attach onRetry at render time so the closure is fresh
    const messagesWithRetry = messages.map((m) =>
        m.isError ? { ...m, onRetry: handleRetry, retryLabel: t('retry_message', {}, 'Retry') } : m,
    );

    /* ---- keyboard send ---- */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    /* ---- create ticket ---- */
    const openTicketSheet = useCallback(() => {
        setTicketError(null);
        setTicketSheetOpen(true);
    }, []);

    const handleTicketConfirm = async () => {
        setTicketLoading(true);
        setTicketError(null);
        try {
            const history = buildHistory(messages);
            await helpCenterService.createTicket(lastAssistantContent, history);
            setTicketSheetOpen(false);
            setTicketSuccess(true);
            // Auto-dismiss success after 4s
            setTimeout(() => setTicketSuccess(false), 4000);
        } catch {
            // Own key — NOT 'chat_error'. That key's real translation is the
            // chat-failure sentence ("Something went wrong…"), used at the
            // two send/retry sites above; reusing it here meant only the
            // English fallback differed, so the moment 'chat_error' resolved
            // from the DB this ticket failure would render the wrong message.
            setTicketError(t('ticket_error', {}, 'Failed to create ticket. Please try again.'));
        } finally {
            setTicketLoading(false);
        }
    };

    /* ---- register trailing action ---- */
    useNavChrome(
        {
            actions: [
                {
                    id: 'create-ticket',
                    label: t('create_ticket', {}, 'Create Ticket'),
                    icon: <TicketIcon />,
                    onPress: openTicketSheet,
                },
            ],
        },
        [openTicketSheet, t],
    );

    /* ---- input change ---- */
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    return (
        <div className="hlp-wrap">
            {/* Success notification */}
            {ticketSuccess && (
                <div className="hlp-success" data-testid="ticket-success" role="status">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t('ticket_created', {}, 'Support ticket created successfully.')}
                </div>
            )}

            {/* Message list */}
            <div className="hlp-messages" role="log" aria-live="polite" aria-label={t('chat_log', {}, 'Chat messages')}>
                {messagesWithRetry.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                ))}

                {/* Typing indicator */}
                {isLoading && <TypingIndicator />}

                {/* Scroll anchor */}
                <div ref={bottomRef} aria-hidden="true" />
            </div>

            {/* Fixed input bar */}
            <div className="hlp-input-bar">
                <input
                    type="text"
                    className="hlp-input"
                    data-testid="chat-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('type_message', {}, 'Type a message…')}
                    disabled={isLoading}
                    aria-label={t('type_message', {}, 'Type a message')}
                    autoComplete="off"
                />
                <button
                    type="button"
                    className="hlp-send-btn"
                    data-testid="send-btn"
                    onClick={() => sendMessage()}
                    disabled={isLoading || !inputValue.trim()}
                    aria-label={t('send_message', {}, 'Send message')}
                >
                    <SendIcon />
                </button>
            </div>

            {/* Create ticket sheet */}
            <ConfirmSheet
                open={ticketSheetOpen}
                onClose={() => { setTicketSheetOpen(false); setTicketError(null); }}
                onConfirm={handleTicketConfirm}
                title={t('create_ticket', {}, 'Create Support Ticket')}
                message={t(
                    'ticket_summary',
                    {},
                    'A support ticket will be created from this conversation for our team to review.',
                )}
                confirmLabel={t('create_ticket', {}, 'Create Ticket')}
                loading={ticketLoading}
            >
                {/* Rendered INSIDE ConfirmSheet's children slot — its real
                    error slot. `error` was passed as a prop here, which
                    ConfirmSheet has never accepted; React dropped it silently
                    and a failed ticket creation showed the user nothing. */}
                {ticketError && (
                    <div className="hlp-ticket-error" data-testid="ticket-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {ticketError}
                    </div>
                )}
            </ConfirmSheet>
        </div>
    );
}
